import { CooldownTrainType, DonoTrain, DonoTrainGoal, DonoTrainSettings, InProgressTrainType, PrimedTrainType } from "@ailenax-overlay/shared";
import { createRef, CSSProperties, FC, ReactNode, Ref, useEffect, useMemo, useRef, useState } from "react";
import { AilenaxBox } from "../AilenaxBox/AilenaxBox";
import { Ready } from "./Ready";
import { Primed } from "./Primed";
import { InProgress } from "./InProgress";
import { Cooldown } from "./Cooldown";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { Finished } from "./Finished";
import { LevelUp } from "./LevelUp";
import classNames from "classnames";
import { DonoLeaderboard } from "../DonoLeaderboard/DonoLeaderboard";
import { useSocketEvent } from "@/hooks/useSocketEvent";
import { useSocketCallback } from "@/hooks/useSocketCallback";
import "./DonoTrainWidget.scss";
import "./themes/Ailenax.scss";
import "./themes/Fuerte.scss";
import "./themes/Bayern.scss";

interface DonoTrainNewProps {
    train: DonoTrain;
    offset: number;
    goals?: DonoTrainGoal[];
    leaderboardTextLeft?: string;
    leaderboardTextRight?: string;
    inConfigMode?: boolean;
    boxMode?: DonoTrainSettings["boxMode"];
}

type WidgetState = DonoTrain["status"] | "finished" | "level-up";

export const DonoTrainWidget: FC<DonoTrainNewProps> = ({ train, offset, goals, leaderboardTextLeft, leaderboardTextRight, inConfigMode, boxMode }) => {
    const [leaderboardShown, setLeaderboardShown] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [showLevelUp, setShowLevelUp] = useState(false);
    const previousLevel = useRef<number>(train.status === "in-progress" ? train.level : 0);
    const previousState = useRef<WidgetState>(train.status);

    const theme = new URLSearchParams(globalThis.location.search).get('theme') ?? undefined;
    const themeCssName = theme === undefined ? undefined : `theme-${theme.toLowerCase()}`;

    const refs: Record<WidgetState, Ref<HTMLDivElement>> = {
        "ready": createRef(),
        "primed": createRef(),
        "in-progress": createRef(),
        "cooldown": createRef(),
        "finished": createRef(),
        "level-up": createRef(),
    }

    const state = useMemo(() => {
        if (showSummary) {
            return "finished";
        } else if (showLevelUp) {
            return "level-up";
        } else {
            return train.status;
        }
    }, [train.status, showSummary, showLevelUp]);

    const element = useMemo(() => {
        //return <Finished train={train as CooldownTrainType} duration={15000}/>;
        switch (state) {
            case "ready":
                return <Ready />;
            case "primed":
                return <Primed train={train as PrimedTrainType} offset={offset} />;
            case "in-progress":
                return <InProgress train={train as InProgressTrainType} offset={offset} />;
            case "cooldown":
                return <Cooldown train={train as CooldownTrainType} offset={offset} />;
            case "finished":
                return <Finished train={train as CooldownTrainType} duration={30000}/>;
            case "level-up":
                return <LevelUp level={(train as InProgressTrainType).level} />
        }
    }, [state, train, offset]);

    useSocketEvent("toggle-leaderboard", (e) => {
        setLeaderboardShown(e.visible);
    });

    useSocketCallback("get-monthly-dono-leaderboard", (visible) => {
        setLeaderboardShown(visible);
    });

    useEffect(() => {
        if (train.status === "cooldown" && previousState.current === "in-progress") {
            setShowSummary(true);

            setTimeout(() => {
                setShowSummary(false);
            }, 30000);
        }
        if (train.status === "in-progress") {
            if (previousState.current === "in-progress") {
                if (train.level > previousLevel.current) {
                    previousLevel.current = train.level;
                    setShowLevelUp(true);

                    setTimeout(() => {
                        setShowLevelUp(false);
                    }, 10000);
                }
            }
            previousLevel.current = train.level;
        }
        previousState.current = train.status;
    }, [train]);

    const trainLevelThreshold = useMemo(() => {
        if (train.status !== "in-progress") {
            return undefined;
        }

        return `threshold-${Math.trunc(train.level / 5)}`;
    }, [train]);

    const boxState = useMemo(() => {
        //return "finished";
        if (state === "level-up") {
            return "in-progress";
        }

        return state;
    }, [state]);

    const currentGoal = useMemo(() => {
        if (inConfigMode) {
            return goals ? goals[0] : { level: 0, goal: "" }
        }
        if (train.status !== "in-progress") {
            return undefined;
        }
        const g = goals ? goals.find(g => g.level > train.level) : undefined;
        return g?.goal.trim() === "" ? undefined : g;
    }, [train, goals, inConfigMode]);

    const elements: ReactNode[] = [];

    const hasLeaderboadFeature = import.meta.env.VITE_FEATURE_LEADERBOARD === true;

    const opacity = useMemo(() => {
        if (inConfigMode) {
            return 1;
        }
        switch (state) {
            case "ready":
                return import.meta.env.VITE_FEATURE_TRAIN_READY_OPACITY;
            case "cooldown":
                if (!leaderboardShown) {
                    return import.meta.env.VITE_FEATURE_TRAIN_COOLDOWN_OPACITY;
                }
                break;
        }

        return 1;
    }, [state, leaderboardShown, inConfigMode]);

    elements.push(
        <AilenaxBox key="train" theme="none" className={classNames("dono-train", boxState, trainLevelThreshold)} style={{ "--opacityDelay": leaderboardShown ? "" : "1000ms", opacity } as CSSProperties}>
            <SwitchTransition>
                <CSSTransition nodeRef={refs[state]} key={state} timeout={1000} classNames="dono-train-state">
                    <div className="h-[100%]" ref={refs[state]}>
                        { element }
                    </div>
                </CSSTransition>
            </SwitchTransition>
        </AilenaxBox>
    );

    const boxContent = useMemo(() => {
        switch (boxMode) {
            case "goal":
                if (currentGoal !== undefined) {
                    return <span>LVL { currentGoal.level }: { currentGoal.goal }</span>;
                }
                break;
            case "record":
                if (train.status === "in-progress" && train.record) {
                    return <span>Rekord: Level { train.record.level }, { train.record.percent.toFixed(2) }%</span>;
                }
                break;
        }
        return null;
    }, [boxMode, currentGoal, train]);

    elements.push(
        <AilenaxBox key="extra-box" className={classNames("dono-train goals text-center", boxState, trainLevelThreshold)} style={{ opacity: boxContent === null ? 0 : 1 }}>
            { boxContent ?? <span>&nbsp;</span>}
        </AilenaxBox>
    );


    if (import.meta.env.VITE_FEATURE_TRAIN_ORDER_REVERSE === true) {
        elements.reverse();
    }

    return (
        <div className={classNames("dono-train-container flex flex-col gap-2 relative font-overlay font-semibold", themeCssName)}>
            { hasLeaderboadFeature && (
                <DonoLeaderboard leaderboardTextLeft={leaderboardTextLeft} leaderboardTextRight={leaderboardTextRight}/>
            )}
            <div className="relative flex flex-col gap-2">
                { elements }
            </div>
        </div>
    );
}